import { deviceDiagnostics } from '../platform/device-diagnostics';
import {
  LOOP_CHANNELS,
  type AudioContextState,
  type BufferAudioSnapshot,
  type LoopChannel,
} from './sound-pack';

// Every theme clip plays from a decoded buffer on one AudioContext. Loops repeat inside the
// render thread, so they never seek a media element at the boundary; WebKit could freeze a
// looping <audio> there, silently and without any media event.
const MAX_VOICES = 12;
const MAX_LATE_MS = 150;
const EFFECT_GAIN = 0.5;
const LOOP_GAIN = 0.25;
const RETRY_MS = 5_000;
// iOS 26 Safari can leave a context reporting `running` with a frozen currentTime after a
// background/return cycle; sources then never end and nothing is audible. One second is
// well above the ~600 ms resume latency measured on device, so a still clock means dead output.
const STALL_MS = 1_000;
type Voice = { source: AudioBufferSourceNode; gain: GainNode };
type Decode = { data: ArrayBuffer; signal: AbortSignal; resolve: (buffer: AudioBuffer | null) => void };
/** A requested loop; `gain` is the theme's 0–1 clip volume. */
export type LoopClip = { readonly src: string; readonly gain: number };

export class BufferAudio {
  private sources = new Set<string>();
  private buffers = new Map<string, Promise<AudioBuffer | null>>();
  private controller = new AbortController();
  private decodeQueue: Decode[] = [];
  private decoding = false;
  private voices = new Set<Voice>();
  private pending = new Set<object>();
  private loopClips = new Map<LoopChannel, LoopClip>();
  private loopVoices = new Map<LoopChannel, Voice>();
  private running = false;
  private retryAfter = new Map<string, number>();
  private clock: { time: number; at: number } | undefined;
  private readonly stateChanged = () => {
    if (deviceDiagnostics) {
      const state: string = this.context.state;
      deviceDiagnostics.record(
        'audio-statechange',
        state === 'running' || state === 'suspended' || state === 'interrupted' || state === 'closed'
          ? state
          : 'unknown',
      );
    }
    if (this.context.state === 'running') return;
    // Never deliver effects queued before an OS interruption when sound returns. Loops pause
    // with the context clock and continue in place.
    this.stopEffects();
    if (this.running && this.context.state !== 'closed') this.resume();
  };

  constructor(
    private context: AudioContext,
    private readonly request: typeof fetch = (input, init) => globalThis.fetch(input, init),
    private readonly now: () => number = () => performance.now(),
  ) {
    this.context.addEventListener('statechange', this.stateChanged);
  }

  /** Forgets the pack: stops, and discards effect sources, buffers, in-flight loads, voices and loops. */
  reset(): void {
    this.stop();
    for (const channel of LOOP_CHANNELS) this.setLoop(channel, undefined);
    this.cancelLoads();
    this.controller = new AbortController();
    this.sources.clear();
    this.buffers.clear();
    this.retryAfter.clear();
  }

  /** Which effect clips play and are prefetched on resume; decoded buffers stay cached. */
  setEffectSources(effectSources: Iterable<string>): void {
    this.sources = new Set(effectSources);
  }

  /** Starts, retunes or stops one channel's loop. A loop starts once running and its buffer is decoded. */
  setLoop(channel: LoopChannel, clip: LoopClip | undefined): void {
    const current = this.loopClips.get(channel);
    if (clip && current?.src === clip.src) {
      this.loopClips.set(channel, clip);
      const voice = this.loopVoices.get(channel);
      if (voice) voice.gain.gain.value = LOOP_GAIN * clip.gain;
      return;
    }
    this.releaseLoop(channel);
    if (!clip) {
      this.loopClips.delete(channel);
      return;
    }
    this.loopClips.set(channel, clip);
    this.startLoop(channel);
  }

  /** True when the context claims to run but its clock has not moved for STALL_MS. */
  stalled(): boolean {
    if (!this.running || this.context.state !== 'running') {
      this.clock = undefined;
      return false;
    }
    const sample = { time: this.context.currentTime, at: this.now() };
    if (!this.clock || sample.at - this.clock.at < STALL_MS) {
      this.clock ??= sample;
      return false;
    }
    const stalled = sample.time === this.clock.time;
    this.clock = sample;
    return stalled;
  }

  /**
   * Swap in a fresh context, keeping decoded clips; the only recovery from a dead render thread.
   * Loops restart from their beginning on the next resume().
   */
  replaceContext(next: AudioContext): void {
    this.context.removeEventListener('statechange', this.stateChanged);
    this.stopEffects();
    for (const channel of LOOP_CHANNELS) this.releaseLoop(channel);
    void this.context.close().catch(() => {});
    this.context = next;
    this.clock = undefined;
    this.context.addEventListener('statechange', this.stateChanged);
    deviceDiagnostics?.record('audio-recreate');
  }

  resume(): void {
    // A preceding suspend may still be queued even if state currently says running.
    const needsResume = !this.running || this.context.state !== 'running';
    this.running = true;
    if (needsResume && this.context.state !== 'closed') {
      this.clock = undefined;
      const settle = deviceDiagnostics?.begin('resume');
      void this.context.resume().then(
        () => settle?.(true),
        () => settle?.(false),
      );
    }
    // Retry only on recovery/interaction, at most once per clip per cooldown.
    // Tile playback continues using the cache, including cached failures.
    for (const [src, retryAt] of this.retryAfter) {
      if (this.now() < retryAt) continue;
      this.retryAfter.delete(src);
      this.buffers.delete(src);
    }
    // Loops load first: they are audible from the start, effects only on the next cue.
    for (const channel of LOOP_CHANNELS) this.startLoop(channel);
    for (const src of this.sources) void this.load(src);
  }

  play(src: string, clipGain: number): void {
    if (!this.running || !this.sources.has(src)) return;
    if (this.pending.size >= MAX_VOICES) this.pending.delete(this.pending.values().next().value!);
    const token = {};
    const requestedAt = this.now();
    this.pending.add(token);
    void this.load(src).then((buffer) => {
      if (
        !this.pending.delete(token) ||
        !buffer ||
        !this.running ||
        this.now() - requestedAt > MAX_LATE_MS ||
        this.context.state !== 'running'
      )
        return;
      if (this.voices.size >= MAX_VOICES) this.releaseEffect(this.voices.values().next().value!);
      const voice = this.startVoice(buffer, EFFECT_GAIN * clipGain, false);
      if (!voice) return;
      this.voices.add(voice);
      voice.source.onended = () => this.releaseEffect(voice);
    });
  }

  /** Silences effect voices and drops pending cues; loops keep playing. */
  stopEffects(): void {
    this.pending.clear();
    for (const voice of this.voices) this.releaseEffect(voice);
  }

  stop(): void {
    this.running = false;
    this.stopEffects();
    const settle = deviceDiagnostics?.begin('suspend');
    void this.context.suspend().then(
      () => settle?.(true),
      () => settle?.(false),
    );
  }

  diagnosticsSnapshot(): BufferAudioSnapshot {
    return {
      state: (['suspended', 'running', 'closed', 'interrupted'].includes(this.context.state)
        ? this.context.state
        : 'unknown') as AudioContextState,
      currentTime: this.context.currentTime,
      sampleRate: this.context.sampleRate,
      running: this.running,
      sources: this.sources.size,
      buffers: this.buffers.size,
      decoding: this.decoding,
      decodeQueued: this.decodeQueue.length,
      voices: this.voices.size,
      pending: this.pending.size,
      loops: { music: this.loopVoices.has('music'), ambience: this.loopVoices.has('ambience') },
    };
  }

  dispose(): void {
    this.context.removeEventListener('statechange', this.stateChanged);
    this.stop();
    for (const channel of LOOP_CHANNELS) this.setLoop(channel, undefined);
    this.cancelLoads();
    this.buffers.clear();
    this.sources.clear();
    this.retryAfter.clear();
    void this.context.close().catch(() => {});
  }

  private startLoop(channel: LoopChannel): void {
    const clip = this.loopClips.get(channel);
    if (!clip || !this.running || this.loopVoices.has(channel)) return;
    void this.load(clip.src).then((buffer) => {
      // setLoop may retune the gain while loading; only a different src supersedes this start.
      const current = this.loopClips.get(channel);
      if (!buffer || !this.running || current?.src !== clip.src || this.loopVoices.has(channel)) return;
      const voice = this.startVoice(buffer, LOOP_GAIN * current.gain, true);
      if (voice) this.loopVoices.set(channel, voice);
    });
  }

  /** Connects and starts one source; undefined (with nodes disconnected) if the graph refuses it. */
  private startVoice(buffer: AudioBuffer, gainValue: number, loop: boolean): Voice | undefined {
    let source: AudioBufferSourceNode | undefined;
    let gain: GainNode | undefined;
    try {
      source = this.context.createBufferSource();
      gain = this.context.createGain();
      source.buffer = buffer;
      source.loop = loop;
      gain.gain.value = gainValue;
      source.connect(gain);
      gain.connect(this.context.destination);
      const settle = deviceDiagnostics?.begin('source');
      try {
        source.start();
        settle?.(true);
      } catch (error) {
        settle?.(false);
        throw error;
      }
      return { source, gain };
    } catch {
      source?.disconnect();
      gain?.disconnect();
      return undefined;
    }
  }

  private load(src: string): Promise<AudioBuffer | null> {
    const cached = this.buffers.get(src);
    if (cached) return cached;
    const signal = this.controller.signal;
    const loading = (async () => {
      try {
        const response = await this.request(src, { signal });
        if (!response.ok || signal.aborted) return null;
        const data = await response.arrayBuffer();
        if (signal.aborted) return null;
        return new Promise<AudioBuffer | null>((resolve) => {
          this.decodeQueue.push({ data, signal, resolve });
          void this.decodeNext();
        });
      } catch {
        return null;
      }
    })();
    void loading.then((buffer) => {
      if (!signal.aborted && !buffer) this.retryAfter.set(src, this.now() + RETRY_MS);
    });
    // Failed clips are cached too; a missing file must not trigger a request per tile.
    this.buffers.set(src, loading);
    return loading;
  }

  private async decodeNext(): Promise<void> {
    if (this.decoding) return;
    this.decoding = true;
    // decodeAudioData itself cannot be aborted. Allow only one in flight, with
    // queued work belonging to the current pack (a bounded set of distinct clips).
    while (this.decodeQueue.length) {
      const job = this.decodeQueue.shift()!;
      let buffer: AudioBuffer | null = null;
      try {
        if (!job.signal.aborted) {
          const settle = deviceDiagnostics?.begin('decode');
          try {
            buffer = await this.context.decodeAudioData(job.data);
            settle?.(true);
          } catch (error) {
            settle?.(false);
            throw error;
          }
        }
      } catch {
        /* Optional clip. */
      }
      job.resolve(job.signal.aborted ? null : buffer);
    }
    this.decoding = false;
  }

  private cancelLoads(): void {
    this.controller.abort();
    for (const job of this.decodeQueue) job.resolve(null);
    this.decodeQueue = [];
  }

  private releaseLoop(channel: LoopChannel): void {
    const voice = this.loopVoices.get(channel);
    if (!voice) return;
    this.loopVoices.delete(channel);
    this.disconnect(voice);
  }

  private releaseEffect(voice: Voice): void {
    if (this.voices.delete(voice)) this.disconnect(voice);
  }

  private disconnect(voice: Voice): void {
    voice.source.onended = null;
    try {
      voice.source.stop();
    } catch {
      /* already stopped */
    }
    voice.source.disconnect();
    voice.gain.disconnect();
  }
}
